import { ConstantValue } from '@calculations/lead-time/strategies/constant-value';
import { PlanningLeadTime } from '@calculations/lead-time/strategies/planning-lead-time';
import { RAFFixedMarket } from '@calculations/lead-time/strategies/raf-fixed-market';
import type { LeadTimeStrategy } from '@calculations/lead-time/types';
import { LeadTimeStrategy as LeadTimeStrategyType } from '@snap-types/models/enums';
import type { LeadTimeConfig } from '@snap-types/models/lead-time-config';
import type { Nullish } from '@snap-types/types/nullish';

import { QuantityAware } from '../strategies/quantity-aware';

/**
 * The site settings a lead time strategy needs on top of its own config. The
 * parameter is required rather than optional so a new strategy that needs a week
 * length has to be handed one instead of quietly assuming seven days.
 */
export type LeadTimeSiteConfig = {
  productionDaysPerWeek: number;
  transitDaysPerWeek: number;
  planningDaysPerWeek: number;
};

export function transformLeadTimeConfigToLeadTimeStrategy(
  leadTimeConfig: Nullish<LeadTimeConfig>,
  siteConfig: LeadTimeSiteConfig,
): LeadTimeStrategy | null {
  if (!leadTimeConfig) {
    return null;
  }

  const { leadTimeType: type } = leadTimeConfig;

  switch (type) {
    case LeadTimeStrategyType.CONSTANT_VALUE:
      return {
        name: ConstantValue,
        config: {
          weeks: leadTimeConfig.weeks,
        },
      };

    case LeadTimeStrategyType.PLANNING_LEADTIME:
      return {
        name: PlanningLeadTime,
        config: {
          planningDaysPerWeek: siteConfig.planningDaysPerWeek,
        },
      };

    case LeadTimeStrategyType.RAF_FIXED_MARKET:
      return {
        name: RAFFixedMarket,
        config: {
          default: { weeks: leadTimeConfig.defaultWeeks },
          nasParts: { weeks: leadTimeConfig.nasPartWeeks },
        },
      };

    case LeadTimeStrategyType.QUANTITY_AWARE:
      return {
        name: QuantityAware,
        config: {
          shiftHours: leadTimeConfig.shiftHours,
          bufferDays: leadTimeConfig.bufferDays,
          moveDayRatio: leadTimeConfig.moveDayRatio,
          ...siteConfig,
        },
      };

    default: {
      const exhaustive: never = type;
      throw new Error(`Unhandled lead time type: ${exhaustive}`);
    }
  }
}
